import {
  Badge,
  Box,
  HStack,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Select,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { FilterBar } from "@/components/FilterBar";
import { MainContainer } from "@/components/mainContainer";
import { MenuActionButton } from "@/components/menuActionButton";
import * as Fonts from "../../../components/texts";
import * as T from "../../../components/table";
import * as Inputs from "../../../components/inputs";
import { useNavigate } from "react-router-dom";
import { Pagination } from "@/components/pagination";
import { SearchIcon } from "@/components/icons";
import { useCustomToast } from "@/hooks/useToast";
import { useEvent } from "@/hooks/useEvent";
import {
  EventEditIndexDto,
  EventIndexDto,
  EventIndexFilterDto,
} from "../types";
import LoadingOverlay from "@/components/overlay";
const initialFilter: EventIndexFilterDto = {
  name: "",
  branch: "",
  type: "",
  team: "",
  date: "",
};

export const Events = () => {
  const navigate = useNavigate();
  const { customToast } = useCustomToast();
  const [page, setPage] = useState<number>(1);
  const { getEvents } = useEvent();
  const [event, setEvent] = useState<EventIndexDto[]>([]);
  const [totalEvent, setTotalEvent] = useState<number>(0);
  const [lastPage, setLastPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<EventIndexFilterDto>(initialFilter);
  const [stopedTyping, setStopedTyping] = useState(false);
  const [typingTimer, setTypingTimer] = useState<NodeJS.Timeout | undefined>();
  const [filterType, setFilterType] = useState("");

  const handleInputChange = () => {
    clearTimeout(typingTimer);

    const newTypingTimer = setTimeout(() => {
      setStopedTyping(true);
    }, 500);

    setTypingTimer(newTypingTimer);
  };

  const callbackEvents = useCallback(() => {
    setIsLoading(true);
    getEvents({ page, ...filter })
      .then((response) => {
        setEvent(response.data.data),
          setTotalEvent(response.data.total),
          setLastPage(response.data.lastPage);
      })
      .catch((error) =>
        customToast("error", [error.response.data.message].join(", "))
      )
      .finally(() => setIsLoading(false));
  }, [page, filter]);

  useEffect(() => callbackEvents(), [page, filter.name, filter.type]);

  useEffect(() => {
    if (stopedTyping) {
      if (page === 1) {
        callbackEvents();
      } else {
        setPage(1);
      }
      setStopedTyping(false);
    }
  }, [filter.name, filter.type, stopedTyping]);

  const handleEdit = (event: EventEditIndexDto) => {
    navigate(`/eventos/editar/${event.id}`, { state: { event } });
  };

  const filters = [
    {
      field: (
        <HStack w="30%" justifyContent="flex-end" key={1} paddingBottom={5}>
          <Text
            fontFamily="Inter"
            fontStyle="normal"
            fontWeight="400"
            fontSize="14px"
            lineHeight="17px"
            color="neutral.500"
          >
            Buscar por:
          </Text>
          <Select
            w="70%"
            h={9}
            borderRadius={4}
            bg="neutral.0"
            onChange={(event) => {
              const selected = event.target.value;
              setFilterType(selected);
            }}
          >
            <option value="">Selecione</option>
            <option value="name">Nome do Evento </option>
            <option value="branchName">Unidade</option>
            <option value="teamName">Pacote</option>
            <option value="typeName">Tipo do Evento</option>
            <option value="clients">Clientes</option>
            <option value="date">Data</option>
          </Select>
        </HStack>
      ),
    },
    {
      field: (
        <InputGroup alignItems="center" key={2} paddingBottom={5}>
          <InputLeftElement pointerEvents="none" top="" left={1}>
            <SearchIcon />
          </InputLeftElement>
          <Inputs.SimpleText
            placeholder="O que você procura?"
            w="100%"
            h={9}
            borderRadius={4}
            textIndent="35px"
            bg="neutral.0"
            onChange={(event) => {
              let value = event.target.value;

              if (filterType === "date") {
                if (value.length < 10) return;

                const [day, month, year] = value.split("/");
                if (day && month && year) {
                  value = `${year}-${month.padStart(2, "0")}-${day.padStart(
                    2,
                    "0"
                  )}`;
                }
              }
              setFilter({ ...filter, [filterType]: value });
              handleInputChange();
            }}
          />
        </InputGroup>
      ),
    },
    {
      field: (
        <HStack w="30%" justifyContent="flex-start" key={3} paddingBottom={5}>
          <Text
            fontFamily="Inter"
            fontStyle="normal"
            fontWeight="400"
            fontSize="14px"
            lineHeight="17px"
            color="neutral.500"
          >
            Status:
          </Text>
          <Select
            w="70%"
            h={9}
            borderRadius={4}
            bg="neutral.0"
            onChange={(event) => {
              const value = event.target.value;

              let situation: boolean | undefined;

              if (value === "aberto") {
                situation = true;
              } else if (value === "cancelado") {
                situation = false;
              } else {
                situation = undefined;
              }

              setFilter({ ...filter, situation });
              handleInputChange();
            }}
          >
            <option value="">Selecione</option>
            <option value="aberto">Aberto</option>
            <option value="cancelado">Cancelado</option>
          </Select>
        </HStack>
      ),
    },
  ];

  return (
    <MainContainer
      height={isLoading || event.length < 9 ? undefined : "100%"}
      title="Eventos"
    >
      {isLoading && <LoadingOverlay />}
      <FilterBar
        title={`Eventos Cadastrados (${totalEvent})`}
        buttons={[
          {
            title: "Novo Evento",
            onClick: () => navigate("/eventos/cadastro"),
          },
        ]}
        filters={filters}
      />

      <Stack p={3} mt="0 !important" w="100%">
        <TableContainer w="100%">
          <Table>
            <Thead bgColor="neutral.300">
              <Tr>
                <Th w="35%">
                  <Fonts.TableTheadText>Nome do Evento</Fonts.TableTheadText>
                </Th>
                <Th textAlign="center">
                  <Fonts.TableTheadText>Unidade</Fonts.TableTheadText>
                </Th>
                <Th textAlign="center">
                  <Fonts.TableTheadText>Pacote</Fonts.TableTheadText>
                </Th>
                <Th textAlign="center">
                  <Fonts.TableTheadText>Tipo do Evento</Fonts.TableTheadText>
                </Th>
                <Th textAlign="center">
                  <Fonts.TableTheadText>Cliente</Fonts.TableTheadText>
                </Th>
                <Th textAlign="center">
                  <Fonts.TableTheadText>Retirada Cvt</Fonts.TableTheadText>
                </Th>
                <Th textAlign="center">
                  <Fonts.TableTheadText>Acesso</Fonts.TableTheadText>
                </Th>
                <Th textAlign="center">
                  <Fonts.TableTheadText>Data</Fonts.TableTheadText>
                </Th>
                <Th textAlign="center">
                  <Fonts.TableTheadText>Status</Fonts.TableTheadText>
                </Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {event.map((event) => {
                const date = new Date(event.date);
                const formattedDate = `${String(date.getDate()).padStart(
                  2,
                  "0"
                )}/${String(date.getMonth() + 1).padStart(
                  2,
                  "0"
                )}/${date.getFullYear()} ${String(date.getHours()).padStart(
                  2,
                  "0"
                )}:${String(date.getMinutes()).padStart(2, "0")}`;
                return (
                  <T.Body key={event.id}>
                    <Td w="35%">
                      <Fonts.TableBodyText>{event.name}</Fonts.TableBodyText>
                    </Td>
                    <Td textAlign="center">
                      <Fonts.TableBodyText>
                        {event.branchName}
                      </Fonts.TableBodyText>
                    </Td>
                    <Td textAlign="center">
                      <Fonts.TableBodyText>
                        {event.teamName}
                      </Fonts.TableBodyText>
                    </Td>
                    <Td textAlign="center">
                      <Fonts.TableBodyText>
                        {event.typeName}
                      </Fonts.TableBodyText>
                    </Td>
                    <Td textAlign="center">
                      <Fonts.TableBodyText>
                        {event.totalClients}
                      </Fonts.TableBodyText>
                    </Td>
                    <Td textAlign="center">
                      <Fonts.TableBodyText>
                        {`${event.totalGuests} / ${event.totalInvitation}`}
                      </Fonts.TableBodyText>
                    </Td>
                    <Td textAlign="center">
                      <Fonts.TableBodyText>
                        {`${event.totalPresentGuests} / ${event.totalInvitation}`}
                      </Fonts.TableBodyText>
                    </Td>
                    <Td textAlign="center">
                      <Fonts.TableBodyText>{formattedDate}</Fonts.TableBodyText>
                    </Td>
                    <Td>
                      <Badge
                        pl={3}
                        mr={3}
                        w={5}
                        h={5}
                        borderRadius="50%"
                        style={{
                          backgroundColor:
                            event.situation === true ? "green" : "red",
                        }}
                      />
                      <Fonts.TableBodyText>
                        {event.situation ? "Aberto" : "Cancelado"}
                      </Fonts.TableBodyText>
                    </Td>
                    <Td>
                      <Menu key={event.id}>
                        <MenuButton>
                          <MenuActionButton color="black" onClick={() => ""} />
                        </MenuButton>
                        <Portal>
                          <MenuList
                            border="1px solid #2F2F2F"
                            marginRight={3}
                            marginTop="-35px"
                          >
                            <MenuItem
                              fontWeight="500"
                              onClick={() => handleEdit(event)}
                            >
                              <Box
                                as="span"
                                w="6px"
                                h="6px"
                                borderRadius="full"
                                bg="#2F2F2F"
                                marginRight={4}
                              />
                              Editar
                            </MenuItem>
                            <MenuItem
                              fontWeight="500"
                              onClick={() =>
                                navigate(`/eventos/${event.id}/clientes`)
                              }
                            >
                              <Box
                                as="span"
                                w="6px"
                                h="6px"
                                borderRadius="full"
                                bg="#2F2F2F"
                                marginRight={4}
                              />
                              Clientes
                            </MenuItem>
                            <MenuItem
                              fontWeight="500"
                              onClick={() =>
                                navigate(
                                  `/eventos/usuarios-portaria/${event.id}`
                                )
                              }
                            >
                              <Box
                                as="span"
                                w="6px"
                                h="6px"
                                borderRadius="full"
                                bg="#2F2F2F"
                                marginRight={4}
                              />
                              Usuário Portaria
                            </MenuItem>
                            <MenuItem
                              fontWeight="500"
                              onClick={() =>
                                navigate(
                                  `/eventos/relatorio-retirada/${event.id}`
                                )
                              }
                            >
                              <Box
                                as="span"
                                w="6px"
                                h="6px"
                                borderRadius="full"
                                bg="#2F2F2F"
                                marginRight={4}
                              />
                              Relatório de Retirada
                            </MenuItem>
                            <MenuItem
                              fontWeight="500"
                              onClick={() =>
                                navigate(
                                  `/eventos/relatorio-acesso/${event.id}`
                                )
                              }
                            >
                              <Box
                                as="span"
                                w="6px"
                                h="6px"
                                borderRadius="full"
                                bg="#2F2F2F"
                                marginRight={4}
                              />
                              Relatório de Acesso
                            </MenuItem>
                          </MenuList>
                        </Portal>
                      </Menu>
                    </Td>
                  </T.Body>
                );
              })}
            </Tbody>
            <Tfoot w="100%"></Tfoot>
          </Table>
          <Pagination page={page} lastPage={lastPage} onClick={setPage} />
        </TableContainer>
      </Stack>
    </MainContainer>
  );
};
